import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ShieldCheck } from "lucide-react";
import { WhatsAppIcon } from "./NavIcons";
import MedEaseMark from "./MedEaseMark";
import drAsthaGhimireImg from "../assets/images/dr-astha-ghimire.png";
import erAshokPaudelImg from "../assets/images/er-ashok-paudel.png";

const MEDEASE_PHONE = "+9779716886567";
const MEDEASE_PHONE_DISPLAY = "+977 971-688-6567";
const MEDEASE_WHATSAPP = "9779716886567";
const MEDEASE_WHATSAPP_LINK = `https://wa.me/${MEDEASE_WHATSAPP}?text=${encodeURIComponent("Hello MedEase Nepal, I would like to inquire about medical equipment.")}`;

function NavModal({ open, onClose, title, children }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="nav-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="nav-modal"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="nav-modal-close" onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
            <h3 className="nav-modal-title">{title}</h3>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [expertsOpen, setExpertsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    const locked = menuOpen || contactOpen || expertsOpen || aboutOpen;
    document.body.style.overflow = locked ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen, contactOpen, expertsOpen, aboutOpen]);

  return (
    <>
      <motion.header
        className={`site-header ${scrolled ? "scrolled" : ""}`}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="nav-stripes" aria-hidden="true">
          <span className="nav-stripe nav-stripe-forest" />
          <span className="nav-stripe nav-stripe-cream" />
          <span className="nav-stripe nav-stripe-silver" />
        </div>

        <nav className="navbar-main">
          <div className="navbar-main-inner container-wide">
            <button
              type="button"
              className="nav-menu-toggle"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>

            <div className="navbar-left">
              <a href="/" className="brand-lockup" onClick={(e) => e.preventDefault()}>
                <span className="brand-mark-wrap">
                  <MedEaseMark size={42} />
                </span>
                <span className="brand-words">
                  <span className="brand-med brand-med-precision">Med</span>
                  <span className="brand-ease">Ease</span>
                  <span className="brand-nepal">Nepal</span>
                </span>
              </a>
            </div>

            <div className="navbar-center">
              <button type="button" className="navbar-link" onClick={() => setExpertsOpen(true)}>
                Our Experts
              </button>
              <button
                type="button"
                className="navbar-link"
                onClick={() => setAboutOpen(true)}
              >
                About Us
              </button>
            </div>

            <div className="navbar-right">
              <a
                href={MEDEASE_WHATSAPP_LINK}
                className="nav-whatsapp-corner"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Message us on WhatsApp"
              >
                <WhatsAppIcon size={28} />
              </a>
            </div>
          </div>
        </nav>
      </motion.header>

      <NavModal open={contactOpen} onClose={() => setContactOpen(false)} title="Contact Us">
        <p className="nav-modal-lead">Speak with our medical equipment team. Lines open for orders &amp; consultations.</p>
        <div className="nav-modal-phone-block">
          <span className="nav-modal-phone-label">Phone</span>
          <a href={`tel:${MEDEASE_PHONE}`} className="nav-modal-phone">
            {MEDEASE_PHONE_DISPLAY}
          </a>
        </div>
        <a href={`tel:${MEDEASE_PHONE}`} className="nav-modal-action nav-modal-action-call">
          <Phone size={18} />
          Call Now
        </a>
      </NavModal>

      <NavModal open={expertsOpen} onClose={() => setExpertsOpen(false)} title="Our Experts">
        <p className="nav-modal-lead">Clinical and technical specialists behind every recommendation.</p>
        <div className="nav-expert-cards">
          <div className="nav-expert-card nav-expert-card--photo">
            <img src={drAsthaGhimireImg} alt="Dr. Astha Ghimire" className="nav-expert-photo" />
            <div className="nav-expert-info">
              <span className="nav-expert-role">Doctor</span>
              <h4 className="nav-expert-name">Dr. Astha Ghimire</h4>
              <p className="nav-expert-degree">MBBS · Clinical Dietician</p>
              <p className="nav-expert-summary">
                Physician and clinical dietician specializing in general health, nutrition, and
                healthy-habit planning.
              </p>
            </div>
          </div>
          <div className="nav-expert-card nav-expert-card--photo">
            <img src={erAshokPaudelImg} alt="Er. Ashok Paudel" className="nav-expert-photo" />
            <div className="nav-expert-info">
              <span className="nav-expert-role">Biomedical Engineer</span>
              <h4 className="nav-expert-name">Er. Ashok Paudel</h4>
              <p className="nav-expert-degree">Biomedical Engineering</p>
              <p className="nav-expert-summary">
                Specialist in medical device selection, installation, calibration, and technical
                support.
              </p>
            </div>
          </div>
        </div>
      </NavModal>

      <NavModal open={aboutOpen} onClose={() => setAboutOpen(false)} title="About Us">
        <div className="nav-modal-badge">
          <ShieldCheck size={15} />
          FDA-Approved · Internationally Certified Equipment
        </div>
        <p className="nav-modal-lead">
          For over a decade, MedEase Nepal has carried trusted healthcare into homes across Nepal —
          from busy city apartments to the quiet corners of rural villages.
        </p>
        <p className="nav-modal-body">
          What began as bedside visits and hand-delivered equipment has become a promise we keep every
          day: that the right care should reach everyone, no matter how far the road. Founded by a
          doctor and a biomedical engineer, we pair world-class mobility, monitoring, and therapeutic
          devices with honest, expert guidance — so every choice feels safe, considered, and dignified.
        </p>
        <p className="nav-modal-body">
          We have been doing this for more than ten years, and since 2026 we are proud to bring the
          same warmth and expertise online — so help is now only a message away.
        </p>
      </NavModal>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <button type="button" className="mobile-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
              <X size={24} />
            </button>
            <div className="mobile-brand brand-lockup">
              <span className="brand-mark-wrap">
                <MedEaseMark size={36} />
              </span>
              <span className="brand-words">
                <span className="brand-med brand-med-precision">Med</span>
                <span className="brand-ease">Ease</span>
                <span className="brand-nepal">Nepal</span>
              </span>
            </div>
            <nav className="mobile-nav">
              <button type="button" className="mobile-nav-link" onClick={() => { setExpertsOpen(true); setMenuOpen(false); }}>
                Our Experts
              </button>
              <button
                type="button"
                className="mobile-nav-link"
                onClick={() => { setAboutOpen(true); setMenuOpen(false); }}
              >
                About Us
              </button>
              <button type="button" className="mobile-nav-link" onClick={() => { setContactOpen(true); setMenuOpen(false); }}>
                Contact Us
              </button>
              <a
                href={MEDEASE_WHATSAPP_LINK}
                className="mobile-nav-link"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
              >
                WhatsApp
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
